import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState, useEffect } from 'react'
import {
  X,
  RotateCcw,
  Ban,
  Film,
  Edit2,
  Plus,
  Trash2,
  Check,
} from 'lucide-react'
import { FieldSnap, PrimaryResult, SnapEvent } from '../../types/fieldCapture'
import { RosterAthleteOption, SecondaryEventType } from '../../types/sports'
import { formatFieldYard, formatNetYards, calculateNetYards } from './fieldYardageUtils'

interface PastSnapDetailModalProps {
  isOpen: boolean
  onClose: () => void
  snap: FieldSnap | null
  onCorrectResult: (params: {
    snapId: string
    newPrimaryResult: PrimaryResult
    passerId?: string | null
    receiverId?: string | null
    tacklerId?: string | null
  }) => Promise<void>
  onVoidSnap: (snapId: string, validity: 'void' | 'no_play', reason?: string) => Promise<void>
  onUpdateSnapDetails?: (params: {
    snapId: string
    patch: Partial<FieldSnap>
    events?: SnapEvent[]
  }) => Promise<void>
  rosterAthletes?: RosterAthleteOption[]
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  pass_attempt: 'Intento de Pase',
  pass_completion: 'Pase Completado',
  target: 'Objetivo de Pase',
  catch: 'Recepción',
  drop: 'Drop (Pase caído)',
  interception_thrown: 'Pase Interceptado (Lanzador)',
  interception: 'Intercepción (Defensa)',
  flag_attempt_success: 'Flag Conseguido',
  flag_attempt_miss: 'Flag Fallado',
  pass_defended: 'Pase Defendido',
  sack: 'Sack',
  pressure: 'Presión / Hurry',
  touchdown: 'Touchdown (+6)',
  conversion_attempt: 'Intento de Conversión',
  conversion_success: 'Conversión Exitosa',
  assignment_error: 'Error de Asignación',
  positive_execution: 'Gran Ejecución',
  penalty: 'Falta / Penalización',
}

export const PastSnapDetailModal: React.FC<PastSnapDetailModalProps> = ({
  isOpen,
  onClose,
  snap,
  onCorrectResult,
  onVoidSnap,
  onUpdateSnapDetails,
}) => {
  const [isEditingResult, setIsEditingResult] = useState(false)
  const [selectedResult, setSelectedResult] = useState<PrimaryResult>('pass_complete')
  const [isVoiding, setIsVoiding] = useState(false)
  const [voidValidity, setVoidValidity] = useState<'void' | 'no_play'>('void')
  const [voidReason, setVoidReason] = useState('')
  const [loadingAction, setLoadingAction] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Modo edición extendida (yardas, down y eventos secundarios)
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [editDown, setEditDown] = useState<number>(1)
  const [editYardStart, setEditYardStart] = useState<number>(5)
  const [editYardEnd, setEditYardEnd] = useState<number | null>(null)
  const [editEvents, setEditEvents] = useState<SnapEvent[]>([])
  const [editIsTouchdown, setEditIsTouchdown] = useState<boolean>(false)
  const [editScorerAthleteId, setEditScorerAthleteId] = useState<string>('')

  useModalLifecycle(isOpen && Boolean(snap), onClose)
  const [editHadLateral, setEditHadLateral] = useState<boolean>(false)

  // Creador rápido de evento dentro del modal
  const [newEventAthleteId, setNewEventAthleteId] = useState<string>('')
  const [newEventType, setNewEventType] = useState<string>('flag_attempt_miss')

  useEffect(() => {
    if (snap) {
      setSelectedResult(snap.primary_result || 'pass_complete')
      setEditDown(snap.down || 1)
      setEditYardStart(snap.yard_line_start !== null && snap.yard_line_start !== undefined ? snap.yard_line_start : 5)
      setEditYardEnd(snap.yard_line_end !== null && snap.yard_line_end !== undefined ? snap.yard_line_end : null)
      setEditEvents(snap.events ? snap.events.filter((e) => !e.is_revoked) : [])
      setEditIsTouchdown(Boolean(snap.is_touchdown))
      setEditScorerAthleteId(snap.scorer_athlete_id || '')
      setEditHadLateral(Boolean(snap.had_lateral))
      setIsEditingDetails(false)
      setIsEditingResult(false)
      setIsVoiding(false)
    }
  }, [snap])

  if (!isOpen || !snap) return null

  const handleConfirmCorrection = async () => {
    setLoadingAction(true)
    setActionError(null)
    try {
      await onCorrectResult({
        snapId: snap.id,
        newPrimaryResult: selectedResult,
        passerId: snap.passer_athlete_id,
        receiverId: snap.receiver_athlete_id,
        tacklerId: snap.tackler_athlete_id,
      })
      setIsEditingResult(false)
      onClose()
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Error al corregir resultado.')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleConfirmVoid = async () => {
    setLoadingAction(true)
    setActionError(null)
    try {
      await onVoidSnap(snap.id, voidValidity, voidReason.trim())
      setIsVoiding(false)
      onClose()
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Error al anular snap.')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleSaveDetailsCorrection = async () => {
    if (!onUpdateSnapDetails) return
    setLoadingAction(true)
    setActionError(null)
    try {
      const netY = editIsTouchdown ? (50 - editYardStart) : calculateNetYards(editYardStart, editYardEnd)
      await onUpdateSnapDetails({
        snapId: snap.id,
        patch: {
          down: editDown,
          yard_line_start: editYardStart,
          yard_line_end: editIsTouchdown ? 50 : editYardEnd,
          net_yards: netY,
          is_touchdown: editIsTouchdown,
          scorer_athlete_id: editIsTouchdown ? editScorerAthleteId || null : null,
          had_lateral: editHadLateral,
        },
        events: editEvents,
      })
      setIsEditingDetails(false)
      onClose()
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Error al guardar correcciones del snap.')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleAddSecondaryEvent = () => {
    if (!newEventAthleteId) {
      setActionError('Selecciona un participante para asociar el evento')
      return
    }
    const participant = (snap.participants || []).find((p) => p.athlete_id === newEventAthleteId)
    const unit = participant ? participant.unit : 'defense'

    const createdEvent: SnapEvent = {
      id: crypto.randomUUID(),
      snap_id: snap.id,
      team_id: snap.team_id,
      event_type: newEventType as SecondaryEventType | 'penalty',
      actor_athlete_id: newEventAthleteId,
      unit,
      is_derived: false,
      is_revoked: false,
      registered_at: new Date().toISOString(),
    }

    setEditEvents((prev) => [...prev, createdEvent])
    setNewEventAthleteId('')
    setActionError(null)
  }

  const handleRemoveEvent = (eventId?: string, idx?: number) => {
    setEditEvents((prev) =>
      prev.filter((e, i) => (eventId ? e.id !== eventId : i !== idx))
    )
  }

  const resultLabels: Record<string, string> = {
    pass_complete: 'Pase Completo',
    pass_incomplete: 'Pase Incompleto',
    interception: 'Intercepción',
    sack: 'Sack',
    run: 'Carrera',
    other: 'Otro',
  }

  const offenseParticipants = (snap.participants || []).filter((p) => p.unit === 'offense')
  const defenseParticipants = (snap.participants || []).filter((p) => p.unit === 'defense')

  const getAthleteNameById = (athleteId?: string | null) => {
    if (!athleteId) return null
    const p = (snap.participants || []).find((x) => x.athlete_id === athleteId)
    if (p) return `${p.athlete_name}${p.dorsal_number ? ` (#${p.dorsal_number})` : ''}`
    return 'Atleta'
  }

  const netYardsCalc = calculateNetYards(snap.yard_line_start, snap.yard_line_end)

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '1rem',
        backdropFilter: 'blur(3px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '580px',
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.25rem',
          boxShadow: 'var(--shadow-card)',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '1px solid var(--surface-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            borderBottom: '1px solid var(--surface-border)',
            paddingBottom: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span
              style={{
                backgroundColor: 'var(--brand-red)',
                color: '#fff',
                fontSize: '0.88rem',
                fontWeight: 700,
                padding: '0.2rem 0.6rem',
                borderRadius: '6px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Snap #{snap.sequence}
            </span>
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                Auditoría Completa de Snap
              </h3>
              <span
                style={{
                  fontSize: '0.72rem',
                  color: 'var(--text-muted)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                Iniciado: {new Date(snap.snap_started_at).toLocaleTimeString()} · Revisión v{snap.revision}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {actionError && (
          <div
            style={{
              backgroundColor: 'var(--brand-red-subtle)',
              border: '1px solid var(--brand-red)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.5rem 0.75rem',
              color: 'var(--brand-red)',
              fontSize: '0.78rem',
              marginBottom: '0.75rem',
            }}
          >
            {actionError}
          </div>
        )}

        {/* 1. ESTADO, DOWN, YARDAS Y GANANCIA */}
        <div
          style={{
            backgroundColor: 'var(--surface-subtle)',
            padding: '0.75rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            marginBottom: '0.85rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Resultado Principal
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.2rem' }}>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                  {snap.primary_result ? resultLabels[snap.primary_result] || snap.primary_result : 'Sin registrar'}
                </span>
                {snap.is_touchdown && (
                  <span
                    style={{
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #d97706',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '4px',
                    }}
                  >
                    Touchdown (+6)
                  </span>
                )}
                {snap.is_conversion && (
                  <span
                    style={{
                      backgroundColor: snap.conversion_result === 'successful' ? '#dcfce7' : '#fee2e2',
                      color: snap.conversion_result === 'successful' ? '#166534' : '#991b1b',
                      border: snap.conversion_result === 'successful' ? '1px solid #86efac' : '1px solid #fca5a5',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '4px',
                    }}
                  >
                    Conversión de {snap.conversion_points_attempted || (snap.conversion_type === '2_points' ? 2 : 1)} pt ({snap.conversion_result === 'successful' ? `Conseguida +${snap.points_awarded || (snap.conversion_points_attempted || 1)}` : 'Fallada 0 pts'})
                  </span>
                )}
              </div>
              {snap.is_touchdown && (
                <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, marginTop: '0.2rem' }}>
                  Anotador: {getAthleteNameById(snap.scorer_athlete_id) || 'Asignado'}
                  {snap.had_lateral ? ' · (Hubo lateral: avance individual pendiente de reparto)' : ' · (Avance íntegro)'}
                </div>
              )}
              {snap.is_conversion && snap.preceding_touchdown_snap_id && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                  Vinculada al Touchdown anterior ({snap.preceding_touchdown_snap_id.slice(0, 8)})
                </div>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              <span
                className={`badge ${
                  snap.validity === 'valid'
                    ? 'badge-success'
                    : snap.validity === 'no_play'
                    ? 'badge-warning'
                    : 'badge-muted'
                }`}
              >
                {snap.validity === 'valid'
                  ? 'Válido'
                  : snap.validity === 'no_play'
                  ? 'No-Play (Penalización)'
                  : 'Anulado (Void)'}
              </span>
            </div>
          </div>

          {/* Fila de Down y Posición de Campo */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.4rem',
              borderTop: '1px solid var(--surface-border)',
              paddingTop: '0.5rem',
              fontSize: '0.78rem',
            }}
          >
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem', display: 'block' }}>Down</span>
              <strong style={{ color: 'var(--text-main)', fontSize: '0.88rem' }}>
                {snap.down ? `${snap.down}º Down` : 'N/D'}
              </strong>
            </div>

            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem', display: 'block' }}>Posición Balón</span>
              <strong style={{ color: 'var(--text-main)' }}>
                {formatFieldYard(snap.yard_line_start)}
                {snap.yard_line_end !== null && snap.yard_line_end !== undefined
                  ? ` → ${formatFieldYard(snap.yard_line_end)}`
                  : ''}
              </strong>
            </div>

            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem', display: 'block' }}>Ganancia Neta</span>
              <strong
                style={{
                  color:
                    netYardsCalc && netYardsCalc > 0
                      ? 'var(--status-success-text)'
                      : netYardsCalc && netYardsCalc < 0
                      ? 'var(--brand-red)'
                      : 'var(--text-main)',
                  fontSize: '0.88rem',
                }}
              >
                {formatNetYards(snap.net_yards ?? netYardsCalc)}
              </strong>
            </div>
          </div>
        </div>

        {/* 2. EDICIÓN EXTENDIDA DE YARDAS, DOWN Y EVENTOS */}
        {isEditingDetails && (
          <div
            style={{
              backgroundColor: 'var(--surface-card)',
              border: '2px solid var(--brand-red)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.85rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                EDITAR DOWN, YARDAS Y EVENTOS SECUNDARIOS
              </span>
              <button
                type="button"
                onClick={() => setIsEditingDetails(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Fila Touchdown y Anotador */}
            <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.65rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', color: editIsTouchdown ? '#92400e' : 'var(--text-main)' }}>
                <input
                  type="checkbox"
                  checked={editIsTouchdown}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setEditIsTouchdown(checked)
                    if (checked) {
                      setEditYardEnd(50)
                      if (!editScorerAthleteId) {
                        setEditScorerAthleteId(snap.receiver_athlete_id || snap.passer_athlete_id || '')
                      }
                    }
                  }}
                />
                <span>Touchdown (+6 puntos)</span>
              </label>

              {editIsTouchdown && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem', marginTop: '0.45rem' }}>
                  <div>
                    <label className="form-label" style={{ fontSize: '0.65rem' }}>Anotador del TD</label>
                    <select
                      value={editScorerAthleteId}
                      onChange={(e) => setEditScorerAthleteId(e.target.value)}
                      className="form-input"
                      style={{ fontSize: '0.74rem', height: '32px' }}
                    >
                      <option value="">Selecciona anotador...</option>
                      {(snap.participants || []).map((p) => (
                        <option key={p.athlete_id || p.position} value={p.athlete_id || ''}>
                          {p.unit === 'offense' ? 'ATQ' : 'DEF'} · {p.position}: {p.athlete_name} #{p.dorsal_number}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.68rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                      <input
                        type="checkbox"
                        checked={editHadLateral}
                        onChange={(e) => setEditHadLateral(e.target.checked)}
                      />
                      <span>Lateral / cambio de portador (reparto manual)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Inputs de Down, Inicio y Fin de Yarda */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.45rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.68rem' }}>Down</label>
                <select
                  value={editDown}
                  onChange={(e) => setEditDown(Number(e.target.value))}
                  className="form-input"
                  style={{ fontSize: '0.8rem', height: '34px' }}
                >
                  <option value={1}>1º Down</option>
                  <option value={2}>2º Down</option>
                  <option value={3}>3º Down</option>
                  <option value={4}>4º Down</option>
                </select>
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '0.68rem' }}>
                  Yarda Inicial ({formatFieldYard(editYardStart)})
                </label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={editYardStart}
                  onChange={(e) => setEditYardStart(Number(e.target.value))}
                  className="form-input"
                  style={{ fontSize: '0.8rem', height: '34px' }}
                />
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '0.68rem' }}>
                  Yarda Final ({editIsTouchdown ? 'Rival 50 (TD)' : formatFieldYard(editYardEnd)})
                </label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  disabled={editIsTouchdown}
                  placeholder={editIsTouchdown ? '50 (TD)' : 'Pendiente'}
                  value={editIsTouchdown ? 50 : editYardEnd !== null ? editYardEnd : ''}
                  onChange={(e) =>
                    setEditYardEnd(e.target.value === '' ? null : Number(e.target.value))
                  }
                  className="form-input"
                  style={{ fontSize: '0.8rem', height: '34px' }}
                />
              </div>
            </div>

            <div style={{ fontSize: '0.74rem', marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
              Ganancia calculada:{' '}
              <strong>{formatNetYards(calculateNetYards(editYardStart, editYardEnd))}</strong>
            </div>

            {/* Lista editable de eventos secundarios */}
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                EVENTOS EN ESTE SNAP ({editEvents.length})
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.35rem' }}>
                {editEvents.map((ev, idx) => (
                  <div
                    key={ev.id || idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'var(--surface-subtle)',
                      padding: '0.35rem 0.6rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      border: '1px solid var(--surface-border)',
                    }}
                  >
                    <span>
                      <strong>{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}</strong>
                      {ev.actor_athlete_id && (
                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>
                          — {getAthleteNameById(ev.actor_athlete_id)}
                        </span>
                      )}
                      {ev.is_derived && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--brand-yellow)', marginLeft: '0.35rem' }}>
                          (derivado)
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEvent(ev.id, idx)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--brand-red)',
                        cursor: 'pointer',
                        padding: '0.2rem',
                      }}
                      title="Eliminar evento"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Añadir nuevo evento en caliente */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1.2fr auto',
                gap: '0.35rem',
                alignItems: 'flex-end',
                borderTop: '1px solid var(--surface-border)',
                paddingTop: '0.5rem',
              }}
            >
              <div>
                <label className="form-label" style={{ fontSize: '0.65rem' }}>Jugador</label>
                <select
                  value={newEventAthleteId}
                  onChange={(e) => setNewEventAthleteId(e.target.value)}
                  className="form-input"
                  style={{ fontSize: '0.74rem', height: '32px' }}
                >
                  <option value="">Selecciona jugador...</option>
                  {(snap.participants || []).map((p) => (
                    <option key={p.athlete_id || p.position} value={p.athlete_id || ''}>
                      {p.unit === 'offense' ? 'ATQ' : 'DEF'} · {p.position}: {p.athlete_name} #{p.dorsal_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '0.65rem' }}>Acción</label>
                <select
                  value={newEventType}
                  onChange={(e) => setNewEventType(e.target.value)}
                  className="form-input"
                  style={{ fontSize: '0.74rem', height: '32px' }}
                >
                  <option value="flag_attempt_success">Flag Conseguido</option>
                  <option value="flag_attempt_miss">Flag Fallado</option>
                  <option value="pressure">Presión / Hurry</option>
                  <option value="sack">Sack</option>
                  <option value="pass_defended">Pase Defendido</option>
                  <option value="drop">Drop</option>
                  <option value="touchdown">Touchdown</option>
                  <option value="penalty">Falta / Penalización</option>
                </select>
              </div>

              <button
                type="button"
                onClick={handleAddSecondaryEvent}
                className="btn btn-secondary"
                style={{ height: '32px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
              >
                <Plus size={13} />
                <span>Añadir</span>
              </button>
            </div>

            {/* Guardar cambios de edición extendida */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditingDetails(false)}
                style={{ fontSize: '0.76rem', padding: '0.3rem 0.6rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loadingAction}
                onClick={handleSaveDetailsCorrection}
                style={{
                  fontSize: '0.76rem',
                  padding: '0.3rem 0.75rem',
                  backgroundColor: 'var(--brand-red)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                }}
              >
                <Check size={14} />
                <span>{loadingAction ? 'Guardando...' : 'Guardar corrección auditada'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Formulario de corrección de resultado principal */}
        {isEditingResult && (
          <div
            style={{
              backgroundColor: 'var(--brand-yellow-subtle)',
              border: '1px solid var(--brand-yellow)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.85rem',
              marginBottom: '1rem',
            }}
          >
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-main)' }}>
              CORREGIR RESULTADO PRINCIPAL
            </span>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.6rem' }}>
              Los eventos derivados del resultado anterior serán revocados y sustituidos por los nuevos de
              forma coherente y auditada.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
              {(['pass_complete', 'pass_incomplete', 'interception', 'sack', 'run', 'other'] as PrimaryResult[]).map(
                (res) => (
                  <button
                    key={res}
                    type="button"
                    onClick={() => setSelectedResult(res)}
                    style={{
                      fontSize: '0.76rem',
                      fontWeight: selectedResult === res ? 700 : 500,
                      padding: '0.45rem',
                      borderRadius: '4px',
                      border: selectedResult === res ? '2px solid var(--brand-red)' : '1px solid var(--surface-border)',
                      backgroundColor: selectedResult === res ? 'var(--brand-red-subtle)' : '#fff',
                      color: selectedResult === res ? 'var(--brand-red)' : 'var(--text-main)',
                      cursor: 'pointer',
                    }}
                  >
                    {resultLabels[res]}
                  </button>
                )
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditingResult(false)}
                style={{ fontSize: '0.76rem', padding: '0.3rem 0.6rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loadingAction}
                onClick={handleConfirmCorrection}
                style={{
                  fontSize: '0.76rem',
                  padding: '0.3rem 0.6rem',
                  backgroundColor: 'var(--brand-red)',
                }}
              >
                {loadingAction ? 'Guardando...' : 'Aplicar corrección auditada'}
              </button>
            </div>
          </div>
        )}

        {/* Formulario de anulación de snap */}
        {isVoiding && (
          <div
            style={{
              backgroundColor: 'var(--surface-subtle)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.85rem',
              marginBottom: '1rem',
            }}
          >
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-main)' }}>
              ANULAR SNAP CONSERVANDO HISTORIAL
            </span>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.5rem' }}>
              El snap no se eliminará de la base de datos para no romper la numeración secuencial.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <input
                  type="radio"
                  name="void_val"
                  checked={voidValidity === 'void'}
                  onChange={() => setVoidValidity('void')}
                />
                Anulación completa (Void)
              </label>
              <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <input
                  type="radio"
                  name="void_val"
                  checked={voidValidity === 'no_play'}
                  onChange={() => setVoidValidity('no_play')}
                />
                Penalización / No-Play
              </label>
            </div>
            <input
              type="text"
              placeholder="Motivo de anulación (ej. fuera de juego, silbato previo)..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="form-input"
              style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsVoiding(false)}
                style={{ fontSize: '0.76rem', padding: '0.3rem 0.6rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loadingAction}
                onClick={handleConfirmVoid}
                style={{
                  fontSize: '0.76rem',
                  padding: '0.3rem 0.6rem',
                  backgroundColor: 'var(--text-secondary)',
                }}
              >
                {loadingAction ? 'Anulando...' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        )}

        {/* Actores Clave Registrados */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.45rem',
            marginBottom: '0.85rem',
            fontSize: '0.76rem',
          }}
        >
          <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '0.45rem', borderRadius: '4px' }}>
            <span style={{ fontSize: '0.66rem', color: 'var(--brand-red)', display: 'block', fontWeight: 700 }}>
              LANZADOR
            </span>
            <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
              {getAthleteNameById(snap.passer_athlete_id) || '(No especificado)'}
            </span>
          </div>

          <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '0.45rem', borderRadius: '4px' }}>
            <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', display: 'block', fontWeight: 700 }}>
              RECEPTOR / TARGET
            </span>
            <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
              {getAthleteNameById(snap.receiver_athlete_id) || '(No especificado)'}
            </span>
          </div>

          <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '0.45rem', borderRadius: '4px' }}>
            <span style={{ fontSize: '0.66rem', color: 'var(--nav-bg-surface)', display: 'block', fontWeight: 700 }}>
              DEFENSOR / TACKLER
            </span>
            <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
              {getAthleteNameById(snap.tackler_athlete_id) || '(No especificado)'}
            </span>
          </div>
        </div>

        {/* Llamadas ofensiva y defensiva */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.85rem' }}>
          <div
            style={{
              padding: '0.6rem',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--surface-border)',
              backgroundColor: 'var(--surface-subtle)',
            }}
          >
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--brand-red)' }}>
              ATAQUE ({snap.offense_lineup_name})
            </span>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              Ejecutada: {snap.offense_call_actual || snap.offense_play_name || 'Sin especificar'}
            </div>
            {snap.offense_call_planned && snap.offense_call_planned !== snap.offense_call_actual && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                Prevista: {snap.offense_call_planned}
              </div>
            )}
          </div>

          <div
            style={{
              padding: '0.6rem',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--surface-border)',
              backgroundColor: 'var(--surface-subtle)',
            }}
          >
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--nav-bg-surface)' }}>
              DEFENSA ({snap.defense_lineup_name})
            </span>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              Ejecutada: {snap.defense_call_actual || snap.defense_call_name || 'Sin especificar'}
            </div>
            {snap.defense_call_planned && snap.defense_call_planned !== snap.defense_call_actual && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                Prevista: {snap.defense_call_planned}
              </div>
            )}
          </div>
        </div>

        {/* Participantes históricos (10 jugadores) */}
        <div style={{ marginBottom: '0.85rem' }}>
          <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            PARTICIPANTES EN ESTE SNAP (10 JUGADORES INMUTABLES)
          </span>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.4rem' }}>
            <div style={{ fontSize: '0.76rem' }}>
              <strong style={{ color: 'var(--brand-red)' }}>Ataque:</strong>
              {offenseParticipants.map((p, idx) => (
                <div key={idx} style={{ color: 'var(--text-main)', marginTop: '0.15rem' }}>
                  {p.position}: {p.athlete_name} {p.dorsal_number ? `#${p.dorsal_number}` : ''}
                  {p.was_substituted && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--brand-yellow)', marginLeft: '0.25rem' }}>
                      (Sub)
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div style={{ fontSize: '0.76rem' }}>
              <strong style={{ color: 'var(--nav-bg-surface)' }}>Defensa:</strong>
              {defenseParticipants.map((p, idx) => (
                <div key={idx} style={{ color: 'var(--text-main)', marginTop: '0.15rem' }}>
                  {p.position}: {p.athlete_name} {p.dorsal_number ? `#${p.dorsal_number}` : ''}
                  {p.was_substituted && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--brand-yellow)', marginLeft: '0.25rem' }}>
                      (Sub)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Eventos secundarios registrados */}
        {snap.events && snap.events.length > 0 && (
          <div style={{ marginBottom: '0.85rem' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
              EVENTOS REGISTRADOS ({snap.events.filter((e) => !e.is_revoked).length})
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
              {snap.events
                .filter((e) => !e.is_revoked)
                .map((ev, idx) => (
                  <span
                    key={ev.id || idx}
                    style={{
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.55rem',
                      borderRadius: '4px',
                      backgroundColor: ev.is_derived ? 'var(--surface-subtle)' : 'var(--brand-yellow-subtle)',
                      border: '1px solid var(--surface-border)',
                      color: 'var(--text-main)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    <span>
                      {EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}
                      {ev.actor_athlete_id && ` (${getAthleteNameById(ev.actor_athlete_id)})`}
                      {ev.is_derived && ' (derivado)'}
                    </span>
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Marca de vídeo si existe */}
        {snap.video_timestamp_mark && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'var(--surface-subtle)',
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.75rem',
              color: 'var(--text-main)',
              marginBottom: '0.85rem',
            }}
          >
            <Film size={15} color="var(--brand-red)" />
            <span>
              Marca en vídeo: <strong>{snap.video_timestamp_mark}</strong>
              {snap.video_segment_id && ` (Segmento ${snap.video_segment_id})`}
            </span>
          </div>
        )}

        {/* Notas del snap */}
        {snap.notes && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.85rem' }}>
            <strong>Nota:</strong> {snap.notes}
          </div>
        )}

        {/* Acciones principales de pie */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {!isEditingResult && !isEditingDetails && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedResult(snap.primary_result || 'pass_complete')
                    setIsEditingResult(true)
                    setIsEditingDetails(false)
                    setIsVoiding(false)
                  }}
                  style={{ fontSize: '0.76rem', minHeight: '38px', padding: '0 0.65rem' }}
                >
                  <RotateCcw size={13} />
                  <span>Resultado</span>
                </button>

                {onUpdateSnapDetails && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditingDetails(true)
                      setIsEditingResult(false)
                      setIsVoiding(false)
                    }}
                    style={{ fontSize: '0.76rem', minHeight: '38px', padding: '0 0.65rem' }}
                  >
                    <Edit2 size={13} />
                    <span>Corregir yardas/eventos</span>
                  </button>
                )}
              </>
            )}

            {!isVoiding && snap.validity === 'valid' && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setIsVoiding(true)
                  setIsEditingResult(false)
                  setIsEditingDetails(false)
                }}
                style={{
                  fontSize: '0.76rem',
                  minHeight: '38px',
                  padding: '0 0.65rem',
                  color: 'var(--status-danger-text)',
                }}
              >
                <Ban size={13} />
                <span>Anular snap</span>
              </button>
            )}
          </div>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ fontSize: '0.8rem', minHeight: '38px', minWidth: '70px' }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
